import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import type { ApiNodeDataOutput } from '@onefootprint/request-types/dashboard';
import { Button, Form, IconButton, InlineAlert } from '@onefootprint/ui';
import { useEffect } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import useStore from '@/components/playbook-builder/hooks/use-store';
import { isTypeable, useValidateExpressions } from '@/components/playbook-builder/hooks/use-validate-expressions';
import { breakingOutputChanges, graphOutputs, outputKey, outputKeyLabel } from '@/components/playbook-builder/utils/outputs';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import { CelNodeFormInstructions, CelNodeFormSubtitle } from '../../../shared/cel-node-form-instructions';
import ExpandableExpressionEditor from '../../../shared/expandable-expression-editor';
import ExpressionTypeHint from '../../../shared/expression-type-hint';
import { OUTPUT_DATA_NAMESPACE, OUTPUT_FORM_ID, OUTPUT_KEY_PATTERN } from '../../output-node.constants';
type OutputFormData = {
  outputs: {
    dataKey: string;
    expression: string;
  }[];
};
type OutputNodeFormProps = {
  node: NodeProps<ApiNodeDataOutput>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
};
export const OutputNodeForm = t0 => {
  const $ = _c(57);
  const {
    node,
    mutation,
    canEdit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "outputNode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    toggle
  } = useDirty();
  const t2 = !canEdit;
  let t3;
  if ($[1] !== node.data.outputs) {
    t3 = node.data.outputs.length ? node.data.outputs.map(_temp) : [{
      dataKey: "",
      expression: ""
    }];
    $[1] = node.data.outputs;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = {
      outputs: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = {
      disabled: t2,
      defaultValues: t4
    };
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const form = useForm(t5);
  const config = useGetNodeConfigContext(node.id, "templatable");
  const autocompleteItems = config?.autocomplete;
  let t6;
  if ($[8] !== form.control) {
    t6 = {
      control: form.control,
      name: "outputs"
    };
    $[8] = form.control;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t6);
  let t7;
  if ($[10] !== form.control) {
    t7 = {
      control: form.control,
      name: "outputs"
    };
    $[10] = form.control;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const watchedOutputs = useWatch(t7);
  let t8;
  if ($[12] !== watchedOutputs) {
    t8 = watchedOutputs ?? [];
    $[12] = watchedOutputs;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const typesByDataKey = useValidateExpressions(node.id, t8);
  const {
    data: t9
  } = useStore();
  const {
    baseOutputs,
    graph
  } = t9;
  const savedTypes = new Map((graphOutputs(graph) ?? []).flat().map(_temp2));
  const pending = (watchedOutputs ?? []).flatMap(t10 => {
    const {
      dataKey: dataKey_0
    } = t10;
    const key = outputKey(dataKey_0);
    const dataType = typesByDataKey.get(dataKey_0)?.dataType ?? savedTypes.get(key)?.dataType;
    return dataType ? [{
      key,
      dataType,
      problems: []
    }] : [];
  });
  const changes = baseOutputs ? breakingOutputChanges([baseOutputs], [pending]) : [];
  const removed = changes.flatMap(_temp3);
  const retyped = changes.filter(_temp4);
  let t11;
  if ($[14] !== form.formState.isDirty || $[15] !== toggle) {
    t11 = () => {
      toggle(OUTPUT_FORM_ID, form.formState.isDirty);
    };
    $[14] = form.formState.isDirty;
    $[15] = toggle;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== form.formState.isDirty) {
    t12 = [form.formState.isDirty];
    $[17] = form.formState.isDirty;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  useEffect(t11, t12);
  let t13;
  if ($[19] !== mutation || $[20] !== node.id) {
    t13 = async data_0 => {
      ;
      try {
        await mutation.mutateAsync({
          kind: "update_node",
          nodeId: node.id,
          nodeData: {
            kind: "output",
            outputs: data_0.outputs.map(_temp5)
          }
        });
      } catch (t14) {
        const _ = t14;
      }
    };
    $[19] = mutation;
    $[20] = node.id;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const handleFormSubmit = t13;
  const T0 = FormProvider;
  const t14 = OUTPUT_FORM_ID;
  let t15;
  if ($[22] !== form || $[23] !== handleFormSubmit) {
    t15 = form.handleSubmit(handleFormSubmit);
    $[22] = form;
    $[23] = handleFormSubmit;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = "flex flex-col gap-3";
  let t17;
  if ($[25] !== t) {
    t17 = t(_temp6);
    $[25] = t;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t17) {
    t18 = <p className="text-label-2 text-primary">{t17}</p>;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <CelNodeFormSubtitle i18nKey={_temp7} />;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t) {
    t20 = t(_temp8);
    $[30] = t;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t) {
    t21 = t(_temp9);
    $[32] = t;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t20 || $[35] !== t21) {
    t22 = <CelNodeFormInstructions subtitle={t19} celSubtitle={t20} instructionsLabel={t21} />;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const t23 = removed.length > 0 && <InlineAlert variant="warning">{t(_temp10, {
      fields: removed.join(", ")
    })}</InlineAlert>;
  const t24 = "flex flex-col gap-4 p-3 border border-tertiary rounded-md";
  const t25 = "flex flex-col gap-5 md:gap-3";
  const t27 = fields.map((field, index) => {
    const row = watchedOutputs?.[index];
    const dataKey_2 = row?.dataKey ?? "";
    const expressionType = typesByDataKey.get(dataKey_2) ?? savedTypes.get(outputKey(dataKey_2));
    const change_1 = retyped.find(c => c.key === outputKey(dataKey_2));
    const retypedMessage = change_1 && t(_temp11, {
      from: change_1.from.join(" or "),
      to: change_1.to.join(" or ")
    });
    const problem = expressionType?.problems[0];
    return <div key={field.id} className="flex flex-wrap items-start gap-3 md:flex-nowrap"><div className="basis-full min-w-0 md:basis-1/3"><Form.Input name={`outputs.${index}.dataKey`} aria-label={t(_temp12)} placeholder={t(_temp13)} size="compact" addon={`${OUTPUT_DATA_NAMESPACE}.`} rules={{
          required: t(_temp14),
          pattern: {
            value: OUTPUT_KEY_PATTERN,
            message: t(_temp15)
          }
        }} /></div><div className="flex-1 min-w-0"><Controller name={`outputs.${index}.expression`} control={form.control} rules={{
          required: t(_temp16)
        }} render={t26 => {
          const {
            field: expressionField,
            fieldState
          } = t26;
          const errorMessage = fieldState.error?.message ?? (problem?.severity === "error" ? problem.message : undefined);
          return <div className="flex flex-col gap-1"><ExpandableExpressionEditor value={expressionField.value ?? ""} onChange={expressionField.onChange} autocompleteItems={autocompleteItems} languageDefinition={config?.languageDefinition} disabled={!canEdit || expressionField.disabled} placeholder={t(_temp17)} hasError={!!errorMessage} labels={{
              expand: t(_temp18),
              dialogTitle: t(_temp19),
              subtitle: <CelNodeFormSubtitle i18nKey={_temp20} />,
              celSubtitle: t(_temp21),
              instructions: t(_temp22)
            }} />{errorMessage && <span className="text-error text-caption-2">{errorMessage}</span>}</div>;
        }} /></div><div className="shrink-0 flex h-8 items-center"><ExpressionTypeHint expressionType={expressionType} problem={problem} isTypeable={isTypeable(row ?? {})} retyped={retypedMessage} /></div>{canEdit && <div className="shrink-0 flex h-8 items-center"><IconButton icon={IcoTrash16} size="compact" variant="ghost" destructive={true} onClick={() => remove(index)} aria-label={t(_temp23)} /></div>}</div>;
  });
  let t28;
  if ($[37] !== t27) {
    t28 = <div className={t25}>{t27}</div>;
    $[37] = t27;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  if ($[39] !== append || $[40] !== canEdit || $[41] !== t) {
    t29 = canEdit && <div><Button fullWidth={false} variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} onClick={() => append({
        dataKey: "",
        expression: ""
      })}>{t(_temp24)}</Button></div>;
    $[39] = append;
    $[40] = canEdit;
    $[41] = t;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] !== t28 || $[44] !== t29) {
    t30 = <div className={t24}>{t28}{t29}</div>;
    $[43] = t28;
    $[44] = t29;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== t14 || $[47] !== t15 || $[48] !== t18 || $[49] !== t22 || $[50] !== t23 || $[51] !== t30) {
    t31 = <form id={t14} onSubmit={t15} className={t16}>{t18}{t22}{t23}{t30}</form>;
    $[46] = t14;
    $[47] = t15;
    $[48] = t18;
    $[49] = t22;
    $[50] = t23;
    $[51] = t30;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] !== T0 || $[54] !== form || $[55] !== t31) {
    t32 = <T0 {...form}>{t31}</T0>;
    $[53] = T0;
    $[54] = form;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  return t32;
};
function _temp(t0) {
  const {
    dataKey,
    expression
  } = t0;
  return {
    dataKey,
    expression
  };
}
function _temp2(data) {
  return [data.key, data];
}
function _temp3(change) {
  return change.kind === "removed" ? outputKeyLabel(change.key) : [];
}
function _temp4(change_0) {
  return change_0.kind === "retyped";
}
function _temp5(t0) {
  const {
    dataKey: dataKey_1,
    expression: expression_0
  } = t0;
  return {
    dataKey: dataKey_1,
    expression: expression_0
  };
}
function _temp6($) {
  return $.title;
}
function _temp7($_0) {
  return $_0.outputNode.celInstructions;
}
function _temp8($_1) {
  return $_1.celSubtitle;
}
function _temp9($_2) {
  return $_2.instructions;
}
function _temp10($_3) {
  return $_3.removedOutputs;
}
function _temp11($_4) {
  return $_4.retypedOutput;
}
function _temp12($_5) {
  return $_5.key.label;
}
function _temp13($_6) {
  return $_6.key.placeholder;
}
function _temp14($_7) {
  return $_7.key.required;
}
function _temp15($_8) {
  return $_8.key.invalidCharacters;
}
function _temp16($_9) {
  return $_9.expression.required;
}
function _temp17($_10) {
  return $_10.expression.placeholder;
}
function _temp18($_11) {
  return $_11.expression.expand;
}
function _temp19($_12) {
  return $_12.expression.label;
}
function _temp20($_13) {
  return $_13.outputNode.celInstructions;
}
function _temp21($_14) {
  return $_14.celSubtitle;
}
function _temp22($_15) {
  return $_15.instructions;
}
function _temp23($_16) {
  return $_16.removeOutput;
}
function _temp24($_17) {
  return $_17.addOutput;
}